import { Button, Col, Flex, Form, InputNumber, Row, Select } from "antd";
import { isEmpty } from "lodash-es";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import type { InfinitySelectOption } from "~components/InfinitySelect";
import { SelectConference } from "~components/SelectConference/SelectConference.tsx";
import { SelectDivision } from "~components/SelectDivision/SelectDivision.tsx";
import { Uniforms } from "~components/Uniforms/Uniforms.tsx";
import { formItemSharedStyles } from "~constants/sharedStyles.ts";
import { EntityStatuses } from "~enums/entityStatuses.ts";
import { PermissionOperations } from "~enums/permissions";
import { SeasonEntityRosterStatuses } from "~enums/seasonEntityRosterStatuses.ts";
import { UniformTypes } from "~enums/uniformTypes.ts";
import usePermissionsByCompetitionName from "~hooks/usePermissionsByCompetitionName";
import { useSeasonEntityService } from "~pages/SeasonEntities/hooks/useSeasonEntityService.ts";
import Page from "../../../Common/Page";
import LoaderContainer from "../../../Common/Page/LoaderContainer";
import PermissionCheck from "../../../Common/PermissionCheck";
import { useGetSeasonEntity } from "./hooks/useGetSeasonEntity";

const SeasonEntityEditPage = () => {
  const { data: seasonEntity, isLoading } = useGetSeasonEntity();
  const { customerId, sport, organizationId, seasonId } = useParams();
  const { formatMessage } = useIntl();
  const [form] = Form.useForm();
  const { upsertSeasonEntity, isUpsertPending: isUpsertingSeasonEntityInProgress } = useSeasonEntityService();
  const { seasonEntityPermissionName } = usePermissionsByCompetitionName();
  const [currentConferenceId, setCurrentConferenceId] = useState<string | undefined>(seasonEntity?.conference?.conferenceId);
  const pageTitle =
    seasonEntity != null
      ? `${seasonEntity.season?.nameLocal ?? seasonEntity.seasonId} - ${seasonEntity.entity?.nameFullLocal ?? seasonEntity.entityId}`
      : formatMessage({ id: "loading" });
  const formInitValues = {
    status: seasonEntity?.status,
    conference: seasonEntity?.conference?.conferenceId
      ? {
          label: seasonEntity.conference.nameLocal,
          value: seasonEntity.conference.conferenceId,
        }
      : null,
    division: seasonEntity?.division?.divisionId
      ? {
          label: seasonEntity.division.nameLocal,
          value: seasonEntity.division.divisionId,
        }
      : null,
    rosterStatus: seasonEntity?.rosterStatus ?? null,
    seed: seasonEntity?.seed ?? null,
  };

  const onConferenceChangedHandler = (conferenceOption: InfinitySelectOption | null) => {
    form.setFieldValue("division", null);
    setCurrentConferenceId(isEmpty(conferenceOption?.value) ? undefined : conferenceOption?.value);
  };

  const onUpdateButtonClick = useCallback(async () => {
    if (!seasonEntity?.entityId) {
      return;
    }

    const formValues = (await form.validateFields()) ?? {};

    await upsertSeasonEntity({
      entityId: seasonEntity.entityId,
      status: formValues.status,
      conferenceId: formValues.conference?.value ?? null,
      divisionId: formValues.division?.value ?? null,
      rosterStatus: formValues.rosterStatus ?? null,
      seed: formValues.seed ?? null,
    });
  }, [form, upsertSeasonEntity, seasonEntity]);

  useEffect(() => {
    setCurrentConferenceId(seasonEntity?.conference?.conferenceId);
  }, [seasonEntity]);

  return (
    <Page title={pageTitle}>
      <LoaderContainer loading={isLoading}>
        <Row>
          <Col span={6}>
            <Flex vertical gap={"middle"}>
              <Form form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} initialValues={formInitValues}>
                <Form.Item
                  name={"status"}
                  label={formatMessage({ id: "status" })}
                  tooltip={formatMessage({ id: "form.help.status.item" }, { item: formatMessage({ id: "entity" }) })}
                  style={formItemSharedStyles}
                  rules={[{ max: 150, required: true }]}
                >
                  <Select
                    options={Object.values(EntityStatuses).map((x) => ({
                      label: x,
                      value: x,
                    }))}
                  />
                </Form.Item>
                <Form.Item name={"conference"} label={formatMessage({ id: "conference" })} style={formItemSharedStyles}>
                  <SelectConference placeholder={""} disabled={false} onChange={onConferenceChangedHandler} />
                </Form.Item>
                <Form.Item name={"division"} label={formatMessage({ id: "division" })} style={formItemSharedStyles}>
                  <SelectDivision conferenceId={currentConferenceId} placeholder={""} disabled={!currentConferenceId} />
                </Form.Item>
                <Form.Item name={"rosterStatus"} label={formatMessage({ id: "rosterStatus" })} style={formItemSharedStyles}>
                  <Select
                    options={Object.values(SeasonEntityRosterStatuses).map((rosterStatus) => ({
                      label: rosterStatus,
                      value: rosterStatus,
                    }))}
                  />
                </Form.Item>
                <Form.Item name={"seed"} label={formatMessage({ id: "seed" })} style={formItemSharedStyles}>
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Form>
              <Flex justify={"flex-end"} gap={"large"}>
                <PermissionCheck permissionKeys={[seasonEntityPermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
                  <Button
                    type="primary"
                    onClick={onUpdateButtonClick}
                    disabled={isUpsertingSeasonEntityInProgress || !seasonEntity?.entityId}
                  >
                    {formatMessage({ id: "entity.update" })}
                  </Button>
                </PermissionCheck>
                <Link to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/entities`}>
                  <Button type="primary">{formatMessage({ id: "close" })}</Button>
                </Link>
              </Flex>
            </Flex>
          </Col>
          <Col offset={4} span={10}>
            {seasonEntity?.entityId && <Uniforms baseType={UniformTypes.Entity} baseId={seasonEntity.entityId} />}
          </Col>
        </Row>
      </LoaderContainer>
    </Page>
  );
};

export default SeasonEntityEditPage;
